/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { SuggestedUtteranceSwagger } from 'src/luis/projects/utterances/services/mappers';
import { LuisId } from 'src/luis/types/Resource';

/**
 * Represents a service for manipulation of suggested
 * endpoint utterances
 */
export const getSuggestedUtteranceService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     */
    const getUrl = () =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .resource('versions', context.versionId);

    /**
     * Gets a list of suggested endpoint utterances.
     *
     * @returns A promise with the suggested endpoint utterances.
     */
    const getSuggestedUtterances = async (resource: 'intents' | 'entities', resourceId: LuisId): Promise<SuggestedUtteranceSwagger[]> => {
        const requestUrl = getUrl()
            .resource(resource, resourceId)
            .resource('suggest')
            .queryParam('multiple-intents', 'true')
            .build();

        const { data } = await httpService.get<SuggestedUtteranceSwagger[]>(requestUrl, apiOptions);

        return data;
    };

    /**
     * Gets a list of suggested endpoint utterances.
     *
     * @returns A promise with the suggested endpoint utterances.
     */
    const getSuggestedUtterancesByRoles = async (entityId: LuisId, roleId: LuisId): Promise<SuggestedUtteranceSwagger[]> => {
        const requestUrl = getUrl()
            .resource('entities', entityId)
            .resource('roles', roleId)
            .resource('suggest')
            .queryParam('multiple-intents', 'true')
            .build();

        const { data } = await httpService.get<SuggestedUtteranceSwagger[]>(requestUrl, apiOptions);

        return data;
    };

    /**
     * Deletes the suggested endpoint utterance with the given id.
     *
     * @param param0.id The id of the suggested endpoint utterance to be deleted.
     */
    const deleteSuggestedUtterance = async (utteranceText: string): Promise<void> => {
        const requestUrl = getUrl()
            .resource('suggest')
            .build();

        await httpService.request('DELETE', requestUrl, JSON.stringify(utteranceText), apiOptions);
    };

    return { getSuggestedUtterances, getSuggestedUtterancesByRoles, deleteSuggestedUtterance };
});
